/**
 * Fixed-height row virtualization (spec §8: the cue table stays 60fps at 10k
 * cues). Pure math so it is unit-testable; the component feeds it scrollTop and
 * viewport height and renders only the returned window, absolutely offset.
 */
export interface VirtualWindow {
  /** First row index to render (inclusive). */
  startIndex: number;
  /** One past the last row index to render (exclusive). */
  endIndex: number;
  /** Pixel offset of the first rendered row from the top of the scroll content. */
  offsetY: number;
  /** Total scrollable height for all rows. */
  totalHeight: number;
}

/**
 * Clamped scrollTop that puts `rowIndex` (0-based) at the top of the viewport;
 * used by the cue table's scroll-to-cue behaviour.
 */
export function scrollTopForRow(
  rowIndex: number,
  rowHeight: number,
  count: number,
  viewportHeight: number,
): number {
  const max = Math.max(0, count * rowHeight - viewportHeight);
  return Math.max(0, Math.min(rowIndex * rowHeight, max));
}

/** Compute the visible row window plus overscan for a fixed-row-height list. */
export function virtualWindow(
  scrollTop: number,
  viewportHeight: number,
  rowHeight: number,
  count: number,
  overscan = 6,
): VirtualWindow {
  const totalHeight = count * rowHeight;
  const clampedTop = Math.max(0, Math.min(scrollTop, Math.max(0, totalHeight - viewportHeight)));
  const first = Math.floor(clampedTop / rowHeight);
  const visible = Math.ceil(viewportHeight / rowHeight);
  const startIndex = Math.max(0, first - overscan);
  const endIndex = Math.min(count, first + visible + overscan);
  return { startIndex, endIndex, offsetY: startIndex * rowHeight, totalHeight };
}
